import React, { useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  Chip,
  Divider,
  TextField,
  Alert,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Business,
  LocationOn,
  Email,
  TrendingUp,
  Build,
  Home,
  Code,
  Send,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Navigation Component with Hamburger Menu
function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (href) => {
    setMobileOpen(false);
    // Smooth scroll to section
    if (href === '#top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navItems = [
    { label: 'Services', href: '#services' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  const drawer = (
    <Box sx={{ width: 280, height: '100%', backgroundColor: '#F2F2F7' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        borderBottom: '1px solid #E5E5EA'
      }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Shiny Blue Moon Ltd."
          onClick={() => handleNavClick('#top')}
          sx={{
            height: '40px',
            width: 'auto',
            maxWidth: '180px',
            objectFit: 'contain',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8
            }
          }}
        />
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{ color: '#1D1D1F' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ pt: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton 
              onClick={() => handleNavClick(item.href)}
              sx={{ 
                py: 2,
                px: 3,
                '&:hover': {
                  backgroundColor: '#E5E5EA'
                }
              }}
            >
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '1.125rem',
                  fontWeight: 500,
                  color: '#1D1D1F'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => handleNavClick('#contact')}
            sx={{ 
              py: 2,
              px: 3,
              mx: 2,
              mt: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0056CC 0%, #4A47B8 100%)',
              }
            }}
          >
            <ListItemText 
              primary="Get Started"
              primaryTypographyProps={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: 'white',
                textAlign: 'center'
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #E5E5EA' }}>
        <Toolbar sx={{ py: { xs: 1, md: 1.5 }, minHeight: { xs: '64px', md: '72px' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Shiny Blue Moon Ltd."
              onClick={() => handleNavClick('#top')}
              sx={{
                height: { xs: '48px', md: '64px' },
                width: 'auto',
                maxWidth: '250px',
                objectFit: 'contain',
                objectPosition: 'center',
                py: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            />
          </Box>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {navItems.map((item) => (
                <Button 
                  key={item.label}
                  color="inherit" 
                  onClick={() => handleNavClick(item.href)}
                  sx={{ 
                    color: '#1D1D1F', 
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#F2F2F7'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button 
                variant="contained" 
                onClick={() => handleNavClick('#contact')}
                sx={{ 
                  background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                  color: 'white',
                  fontWeight: 500,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0056CC 0%, #4A47B8 100%)',
                  }
                }}
              >
                Contact
              </Button>
            </Box>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#1D1D1F' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: '#F2F2F7'
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

// Contact Form Component with reCAPTCHA v3
function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      setSubmitStatus('recaptcha-error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA v3
      const token = await executeRecaptcha('contact_form');
      
      // In a real application, you would send the token to your backend for verification
      // For now, we'll proceed with the mailto approach
      console.log('reCAPTCHA token:', token);

      // Create mailto link with form data
      const subject = encodeURIComponent(formData.subject || 'Website Contact Form');
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}\n\nreCAPTCHA Token: ${token}`
      );
      const mailtoLink = `mailto:property-directors@shinybluemoon.com?subject=${subject}&body=${body}`;
      
      window.location.href = mailtoLink;
      setSubmitStatus('success');
      
      // Reset form
      setFormData({ name: '', email: '', subject: '', message: '' });
      
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      setSubmitStatus('recaptcha-error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ p: 4, height: '100%', backgroundColor: 'white' }}>
      <CardContent sx={{ p: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Send us a Message
        </Typography>
        
        {submitStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            Thank you! Your email client should open with your message.
          </Alert>
        )}
        
        {submitStatus === 'recaptcha-error' && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            Security verification failed. Please try again.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Your Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Your Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Message"
            name="message"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleInputChange}
            required
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Send />}
            disabled={isSubmitting}
            sx={{ 
              py: 1.5,
              fontSize: '1.125rem',
              fontWeight: 500,
              mb: 3
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 2, 
          backgroundColor: '#F2F2F7', 
          borderRadius: 2,
          mb: 2
        }}>
          <Email sx={{ mr: 2, color: '#007AFF' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            property-directors@shinybluemoon.com
          </Typography>
        </Box>
        
        <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block', textAlign: 'center' }}>
          We'll respond within 24 hours during business days.
          <br />
          <em>Protected by reCAPTCHA v3</em>
        </Typography>
      </CardContent>
    </Card>
  );
}

function App() {
  // Replace with your actual reCAPTCHA v3 site key from environment variable
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY; // Test key fallback
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <Box sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Navigation />

        {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: '80px', md: '88px' }, // Account for fixed navbar height
        }}
      >
        {/* Video Background */}
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: -2,
          }}
        >
          <source src="/bg_oai.mp4" type="video/mp4" />
        </Box>
        
        {/* Overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(29, 29, 31, 0.5) 100%)',
            zIndex: -1,
          }}
        />
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { md: 4 } }}>
                <Typography 
                  variant="h1" 
                  gutterBottom 
                  sx={{ 
                    mb: 3,
                    color: 'white',
                    fontWeight: 700,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  Premium Real Estate & Technology Solutions
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4, 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontWeight: 400,
                    lineHeight: 1.4,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}
                >
                  Specializing in property acquisition, renovation, rental management, and innovative 
                  SaaS applications for the renovation market across Edinburgh's prime real estate markets.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    href="#services"
                    sx={{ 
                      px: 4,
                      py: 1.5,
                      fontSize: '1.125rem'
                    }}
                  >
                    Explore Services
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    href="#contact"
                    sx={{ 
                      px: 4,
                      py: 1.5,
                      fontSize: '1.125rem'
                    }}
                  >
                    Get in Touch
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 300, textAlign: 'center', color: 'white' }}>
                  Edinburgh's Premier<br />Property & Tech Company
                </Typography>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -30,
                    left: -30,
                    width: 150,
                    height: 150,
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '50%',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }} id="services">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>
            Our Services
          </Typography>
          <Typography variant="h5" sx={{ color: '#6D6D70', fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
            Comprehensive solutions for property and technology needs
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #007AFF 0%, #40A9FF 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Home sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                  Property Acquisition
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Strategic identification and acquisition of high-potential properties 
                  in Edinburgh's growing real estate market with comprehensive market analysis.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Market Analysis" size="small" />
                  <Chip label="Investment Strategy" size="small" />
                  <Chip label="Due Diligence" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #5856D6 0%, #7B7AE8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Build sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                  Renovation & Development
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Comprehensive project management and renovation services to maximize 
                  property value through strategic improvements and modern upgrades.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Project Management" size="small" />
                  <Chip label="Design & Build" size="small" />
                  <Chip label="Quality Assurance" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #34C759 0%, #5DD579 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <TrendingUp sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                  Rental Management
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Professional property management and rental optimization to ensure 
                  consistent returns and exceptional tenant satisfaction.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Tenant Relations" size="small" />
                  <Chip label="Maintenance" size="small" />
                  <Chip label="Financial Reporting" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid> */}
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 4, position: 'relative' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #8E8E93 0%, #AEAEB2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Code sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h4">
                    Software as a Service
                  </Typography>
                  <Chip 
                    label="Coming Soon" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#007AFF', 
                      color: 'white',
                      fontWeight: 500
                    }} 
                  />
                </Box>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Custom self serve software solutions and digital services for the property management sector - our innovative technology 
                  division currently in development to serve modern business needs.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Web Development" size="small" />
                  <Chip label="Mobile Apps" size="small" />
                  <Chip label="Digital Solutions" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* About Section */}
      <Box sx={{ backgroundColor: '#F2F2F7', py: 10 }} id="about">
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" gutterBottom sx={{ mb: 3 }}>
                About Shiny Blue Moon Ltd.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.125rem', mb: 3 }}>
                We are a dynamic company specializing in property acquisition & 
                comprehensive renovation services across 
                Edinburgh's thriving property market. We're also expanding into technology 
                services with our innovative software development division.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.125rem', mb: 4 }}>
                Our experienced team combines real estate expertise with cutting-edge technology 
                solutions to deliver exceptional results across multiple industries.
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                <Chip 
                  label="Real Estate Expertise" 
                  sx={{ 
                    backgroundColor: '#007AFF', 
                    color: 'white',
                    fontWeight: 500,
                    px: 1
                  }} 
                />
                <Chip 
                  label="Quality Renovations" 
                  sx={{ 
                    backgroundColor: '#5856D6', 
                    color: 'white',
                    fontWeight: 500,
                    px: 1
                  }} 
                />
                <Chip 
                  label="Professional Management" 
                  sx={{ 
                    backgroundColor: '#34C759', 
                    color: 'white',
                    fontWeight: 500,
                    px: 1
                  }} 
                />
                <Chip 
                  label="Technology Innovation" 
                  sx={{ 
                    backgroundColor: '#8E8E93', 
                    color: 'white',
                    fontWeight: 500,
                    px: 1
                  }} 
                />
              </Box>
              
              <Button
                variant="contained"
                size="large"
                href="#contact"
                sx={{ fontSize: '1.125rem', px: 4, py: 1.5 }}
              >
                Learn More About Us
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ pl: { md: 4 } }}>
                <Card sx={{ p: 4, textAlign: 'center', backgroundColor: 'white' }}>
                  <Typography variant="h2" sx={{ color: '#007AFF', mb: 1, fontWeight: 700 }}>
                    Since 2024
                  </Typography>
                  <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                    Serving Edinburgh's Markets
                  </Typography>
                  <Divider sx={{ my: 3, backgroundColor: '#E5E5EA' }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    Committed to excellence in property services and innovative 
                    technology solutions, serving Edinburgh and beyond with 
                    professional expertise across multiple sectors.
                  </Typography>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Directors Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>
            Leadership Team
          </Typography>
          <Typography variant="h5" sx={{ color: '#6D6D70', fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
            Experienced professionals driving innovation and excellence
          </Typography>
        </Box>
        
        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #34C759 0%, #5856D6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 600 }}>
                    SR
                  </Typography>
                </Box>
                
                <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
                  Shiny Rajadurai
                </Typography>
                <Typography variant="h6" sx={{ color: '#34C759', mb: 3, fontWeight: 500 }}>
                  Managing Director & CEO
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Extensive experience specializing in real estate investment, strategic 
                  property renovation & interior refurbishment and project management with focus on maximizing investment returns and operational excellence. Leading our operations and business dvelopment activities.
                </Typography>
                
                <Box sx={{ 
                  backgroundColor: '#F2F2F7', 
                  borderRadius: 2, 
                  p: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block', mb: 0.5 }}>
                    <strong>Appointed:</strong> 23 August 2024
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block' }}>
                    <strong>Residence:</strong> Scotland
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 600 }}>
                    NM
                  </Typography>
                </Box>
                
                <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
                  Neelamuhilvannan Mayavannan
                </Typography>
                <Typography variant="h6" sx={{ color: '#007AFF', mb: 3, fontWeight: 500 }}>
                  Director
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Extensive experience in software development and exploring real estate investment and property development across Scotland. 
                  - Leading our technology division expansion.
                </Typography>
                
                <Box sx={{ 
                  backgroundColor: '#F2F2F7', 
                  borderRadius: 2, 
                  p: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block', mb: 0.5 }}>
                    <strong>Appointed:</strong> 23 August 2024
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8E8E93', display: 'block' }}>
                    <strong>Residence:</strong> Scotland
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Contact Section */}
      <Box sx={{ py: 10, backgroundColor: 'white' }} id="contact">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>
              Get in Touch
            </Typography>
            <Typography variant="h5" sx={{ color: '#6D6D70', fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
              Ready to discuss your property or technology needs?
            </Typography>
          </Box>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%', backgroundColor: '#F2F2F7' }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                    Company Information
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <Business sx={{ mr: 2, color: '#007AFF', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          Company Status
                        </Typography>
                        <Typography variant="body2">
                          Active Private Limited Company
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <LocationOn sx={{ mr: 2, color: '#007AFF', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          Registered Office
                        </Typography>
                        <Typography variant="body2">
                          5 South Charlotte Street<br />
                          Edinburgh, Scotland, EH2 4AN
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ pt: 2, borderTop: '1px solid #E5E5EA' }}>
                      <Typography variant="caption" sx={{ color: '#8E8E93' }}>
                        <strong>Incorporated:</strong> 23 August 2024<br />
                        <strong>Nature of Business:</strong> Real Estate & Technology Services
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <ContactForm />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1D1D1F 0%, #2D2D30 100%)', 
        color: 'white', 
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Logo and Company Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    width: '220px',
                    height: '80px',
                    backgroundColor: 'white',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    p: 1
                  }}
                >
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="Shiny Blue Moon Ltd."
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'white', mb: 2 }}>
                  Shiny Blue Moon Ltd.
                </Typography>
                <Typography variant="body2" sx={{ color: '#AEAEB2', lineHeight: 1.6 }}>
                  Premium real estate and technology solutions in Edinburgh, Scotland. 
                  Delivering excellence across property and digital innovation.
                </Typography>
              </Box>
            </Grid>

            {/* Services */}
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  Property Acquisition
                </Typography>
                <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  Renovation & Development
                </Typography>
                {/* <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  Rental Management
                </Typography> */}
                <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  Software Development
                </Typography>
              </Box>
            </Grid>

            {/* Company */}
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  About Us
                </Typography>
                <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  Leadership Team
                </Typography>
                <Typography variant="body2" sx={{ color: '#AEAEB2', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                  Contact
                </Typography>
              </Box>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                Get in Touch
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Email sx={{ mr: 2, color: '#007AFF' }} />
                  <Typography variant="body2" sx={{ color: '#AEAEB2' }}>
                    property-directors@shinybluemoon.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <LocationOn sx={{ mr: 2, color: '#007AFF', mt: 0.2 }} />
                  <Typography variant="body2" sx={{ color: '#AEAEB2' }}>
                    5 South Charlotte Street<br />
                    Edinburgh, Scotland, EH2 4AN
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                href="#contact"
                sx={{
                  background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5
                }}
              >
                Contact Us
              </Button>
            </Grid>
          </Grid>

          {/* Bottom Bar */}
          <Box sx={{ 
            borderTop: '1px solid #48484A', 
            mt: 6, 
            pt: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2
          }}>
            <Typography variant="caption" sx={{ color: '#8E8E93' }}>
              © 2024 Shiny Blue Moon Ltd. All rights reserved. | 
              Registered in Scotland | Company Registration: SC820371
            </Typography>
            <Typography variant="caption" sx={{ color: '#8E8E93' }}>
              Incorporated: 23 August 2024 | Real Estate & Technology Services
            </Typography>
          </Box>
        </Container>
      </Box>
      </Box>
    </GoogleReCaptchaProvider>
  );
}

export default App;